import { KeyedPaginatedResponse } from '../../../_core/models/keyed-paginated.response';
import { LoadingModel } from '../../../_core/state/loading.model';
import {
  ClearRepliesByParentId, CloseTextarea,
  DeleteComment, DeleteCommentReply,
  GetCommentsByParentId,
  GetCommentsByRecordId, GetCommentsCount, OpenTextarea, PostComment, PostReply,
  VoteComment,
} from './comments.actions';
import { CommentsService } from '../../services/comments/comments.service';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import { append, iif, insertItem, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { tap } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { Comment } from '../../services/comments/models/comment.model';
import { ToastService } from '../../../_core/services/toast.service';
import { HttpErrorResponse } from '@angular/common/http';
import { AnalyticsService } from '../../../_core/analytics/analytics.service';

export type CommentsStateModel = KeyedPaginatedResponse<Comment> & LoadingModel & {
  isLoadingReplies: string[];
  isCreatingComment: boolean;
  isCreatingReplies: string[];
  commentsCount: number;
  recordId: string;
  openTextarea: string;
};

export const initialState: CommentsStateModel = {
  isLoading: false,
  isLoadingReplies: [],
  isCreatingComment: false,
  isCreatingReplies: [],
  data: [],
  lastEvaluatedKey: null,
  commentsCount: 0,
  recordId: '',
  openTextarea: '',
};

@State<CommentsStateModel>({
  name: 'comments',
  defaults: initialState,
})
@Injectable()
export class CommentsState {
  private readonly commentsService = inject(CommentsService);
  private readonly toastService = inject(ToastService);
  private readonly analyticsService = inject(AnalyticsService);

  @Selector()
  static comments(state: CommentsStateModel) {
    return state.data;
  }

  @Selector()
  static commentsCount(state: CommentsStateModel) {
    return state.commentsCount;
  }

  @Selector()
  static lastEvaluatedKey(state: CommentsStateModel) {
    return state.lastEvaluatedKey;
  }

  @Selector()
  static isLoading(state: CommentsStateModel) {
    return state.isLoading;
  }

  @Selector()
  static isLoadingReplies(state: CommentsStateModel) {
    return state.isLoadingReplies;
  }

  @Selector()
  static isCreatingComment(state: CommentsStateModel) {
    return state.isCreatingComment;
  }

  @Selector()
  static isCreatingReplies(state: CommentsStateModel) {
    return state.isCreatingReplies;
  }

  @Selector()
  static openTextarea(state: CommentsStateModel) {
    return state.openTextarea;
  }

  @Action(GetCommentsByRecordId)
  getCommentsByRecordId(context: StateContext<CommentsStateModel>, { recordId }: GetCommentsByRecordId) {
    const { lastEvaluatedKey, recordId: lastRecordId } = context.getState();
    context.setState(patch<CommentsStateModel>({
      isLoading: true,
      recordId,
    }));

    if (recordId !== lastRecordId) {
      context.dispatch(new GetCommentsCount(recordId));
    }

    return this.commentsService.getCommentsByRecordId(recordId, recordId === lastRecordId ? lastEvaluatedKey : undefined).pipe(
      tap((response) => {
        context.setState(patch<CommentsStateModel>({
          data: iif(recordId !== lastRecordId, response.data, append(response.data)),
          lastEvaluatedKey: response.lastEvaluatedKey,
        }));
      }),
      finalize(() => {
        context.setState(patch({ isLoading: false }));
      }),
    );
  }

  @Action(GetCommentsByParentId)
  getCommentsByParentId(context: StateContext<CommentsStateModel>, { recordId, parentId, lastEvaluatedKey }: GetCommentsByParentId) {
    const { data, isLoadingReplies } = context.getState();
    const parentComment = data.find((c) => c.id === parentId);
    context.setState(patch({ isLoadingReplies: [...isLoadingReplies, parentId] }));

    return this.commentsService.getCommentsByParentId(recordId, parentId, lastEvaluatedKey).pipe(
      tap((response) => {
        context.setState(patch<CommentsStateModel>({
          data: updateItem((c) => c.id === parentId, patch({
            replies: {
              lastEvaluatedKey: response.lastEvaluatedKey,
              data: [...parentComment.replies?.data ?? [], ...response.data]
            }
          })),
        }));
      }),
      finalize(() => {
        context.setState(patch({ isLoadingReplies: isLoadingReplies.filter((id) => id !== parentId) }));
      }),
    );
  }

  @Action(GetCommentsCount)
  getCommentsCount(context: StateContext<CommentsStateModel>, { recordId }: GetCommentsCount) {
    context.setState(patch<CommentsStateModel>({
      isLoading: true,
    }));

    return this.commentsService.getCommentsCount(recordId).pipe(
      tap((commentsCount) => {
        context.setState(patch({ commentsCount }));
      }),
      finalize(() => {
        context.setState(patch({ isLoading: false }));
      })
    );
  }

  @Action(PostComment)
  postComment(context: StateContext<CommentsStateModel>, { recordId, userId, comment }: PostComment) {
    const currentCount = context.getState().commentsCount;
    context.setState(patch({ isCreatingComment: true }));

    return this.commentsService.postComment(recordId, comment, userId).pipe(
      tap((createdComment) => {
        this.analyticsService.sendUserActionEvent('send_comment', true);

        if (createdComment.status === 'PENDING') {
          this.toastService.showToast('messages.comments.approval', 'info');
          return;
        }

        this.toastService.showToast('messages.comments.submitted');

        context.setState(patch({
          data: insertItem(createdComment, 0),
          commentsCount: currentCount + 1,
        }));
      }),
      catchError((error, caught) => {
        this.analyticsService.sendUserActionEvent('send_comment', false);

        if (error instanceof HttpErrorResponse) {
          if (error.status === 500) {

            if (error.error.description.includes('Comment exceeds the maximum allowed size due to special characters or emojis')) {
              this.toastService.showToast('messages.comments.exceedsMaximumSize', 'error');
            }
          }
        }

        throw caught;
      }),
      finalize(() => {
        context.setState(patch({ isCreatingComment: false }));
      })
    )
  }

  @Action(PostReply)
  postReply(context: StateContext<CommentsStateModel>, { recordId, userId, comment, parentId, rootCommentId, replyUserId }: PostReply) {
    /*
     * There are several cases when creating a reply to a comment.
     * - If the parent hasn't got any reply (it is its first reply): Just update the replies' count.
     * - If the parent has any reply but replies are collapsed: Just update the replies' count.
     * - If the parent has <= 10 replies, and they are expanded (doesn't render the 'View more' button): Update the replies' count and add the reply to the array to visualize it immediately.
     * - If the parent has > 10 replies, and they are expanded (it renders the 'View more' button): Just update the replies' count.
     * - If the parent has > 10 replies, and they are fully expanded (all replies are visible, no 'View more' button is rendered): Update the replies' count and add the reply to the array.
     */

    const { data, isCreatingReplies } = context.getState();
    const parentComment = data.find((c) => c.id === rootCommentId);

    // Update the array to render the skeleton if LEK is null (no more to load = no 'View more' button) and has any reply loaded (they are expanded)
    if (parentComment.replies?.lastEvaluatedKey === null && parentComment.replies?.data?.length > 0) {
      context.setState(patch({ isCreatingReplies: [...isCreatingReplies, rootCommentId] }));
    }

    return this.commentsService.postReply(recordId, comment, userId, parentId, rootCommentId, replyUserId).pipe(
      tap((createdComment) => {
        this.analyticsService.sendUserActionEvent('send_comment', true);

        if (createdComment.status === 'PENDING') {
          this.toastService.showToast('messages.comments.approval', 'info');
          return;
        }

        this.toastService.showToast('messages.comments.submitted');

        // Always update the count
        context.setState(patch({
          data: updateItem((c) => c.id === parentComment.id, patch({
            repliesApprovedCount: parentComment.repliesApprovedCount + 1,
          })),
        }));

        // Append the reply if LEK is null (no more to load = no 'View more' button) and has any reply loaded (they are expanded)
        if (parentComment.replies?.lastEvaluatedKey === null && parentComment.replies?.data?.length > 0) {
          context.setState(patch({
            data: updateItem((c) => c.id === parentComment.id, patch({
              replies: patch({
                data: [...parentComment.replies?.data ?? [], createdComment],
              }),
            })),
          }));
        }
      }),
      catchError((error, caught) => {
        this.analyticsService.sendUserActionEvent('send_comment', false);

        if (error instanceof HttpErrorResponse) {
          if (error.status === 400) {

            if (error.error.description.includes('You cannot reply to your own comment')) {
              this.toastService.showToast('messages.comments.cannotReplyToYourselfError', 'error');
            }
          }

          if (error.status === 500) {
            if (error.error.description.includes('Comment exceeds the maximum allowed size due to special characters or emojis')) {
              this.toastService.showToast('messages.comments.exceedsMaximumSize', 'error');
            }
          }
        }

        throw caught;
      }),
      finalize(() => {
        context.setState(patch({ isCreatingReplies: isCreatingReplies.filter((id) => id !== rootCommentId) }));
      })
    )
  }

  @Action(ClearRepliesByParentId)
  clearRepliesByParentId(context: StateContext<CommentsStateModel>, { parentId }: ClearRepliesByParentId) {
    context.setState(patch<CommentsStateModel>({
      data: updateItem((c) => c.id === parentId, patch({
        replies: {
          lastEvaluatedKey: null,
          data: [],
        },
      })),
    }));
  }

  @Action(VoteComment)
  voteComment(context: StateContext<CommentsStateModel>, { recordId, commentId, userId, like, rootCommentId }: VoteComment) {
    // Need to request `rootCommentId` for reply votes.
    const currentState = context.getState();

    // If `rootCommentId` exists, that means that we voted a reply.
    let comment: Comment;
    if (rootCommentId) {
      // Find reply inside parent comment.
      comment = currentState.data.find((c) => c.id === rootCommentId).replies.data.find((c) => c.id === commentId);
    } else {
      comment = currentState.data.find((c) => c.id === commentId);
    }

    const { likes, dislikes } = comment;

    return this.commentsService.voteComment(recordId, commentId, userId, like).pipe(
      tap(() => {
        // Update reply
        if (rootCommentId) {
          context.setState(patch({
            data: updateItem((c) => c.id === rootCommentId, patch({ // parent comment
              replies: patch({
                data: updateItem((c) => c.id === commentId, patch({ // update reply
                  likes: like ? likes + 1 : likes,
                  dislikes: !like ? dislikes + 1 : dislikes,
                })),
              }),
            })),
          }));
        } else {
          context.setState(patch({
            data: updateItem((c) => c.id === commentId, patch({
              likes: like ? likes + 1 : likes,
              dislikes: !like ? dislikes + 1 : dislikes,
            })),
          }));
        }

        this.toastService.showToast('messages.comments.rate');
        this.analyticsService.sendUserActionEvent(like ? 'like_comment' : 'dislike_comment', true);
      }),
      catchError((error, caught) => {
        this.analyticsService.sendUserActionEvent(like ? 'like_comment' : 'dislike_comment', false);
        throw caught;
      }),
    );
  }

  @Action(DeleteComment)
  deleteComment(context: StateContext<CommentsStateModel>, { commentId }: DeleteComment) {
    const currentCount = context.getState().commentsCount;

    return this.commentsService.deleteComment(commentId).pipe(
      tap(() => {
        context.setState(patch({
          data: removeItem((c) => c.id === commentId),
          commentsCount: currentCount - 1,
        }))

        this.analyticsService.sendUserActionEvent('delete_comment', true);
      }),
      catchError((error, caught) => {
        this.analyticsService.sendUserActionEvent('delete_comment', false);
        throw caught;
      }),
    );
  }

  @Action(DeleteCommentReply)
  deleteCommentReply(context: StateContext<CommentsStateModel>, { commentId, rootCommentId }: DeleteCommentReply) {
    return this.commentsService.deleteComment(commentId).pipe(
      tap(({ repliesApprovedCount }) => {
        context.setState(patch({
          data: updateItem((c) => c.id === rootCommentId, patch({
            repliesApprovedCount,
            replies: patch({
              data: removeItem((c) => c.id === commentId),
            }),
          })),
        }));

        this.analyticsService.sendUserActionEvent('delete_comment', true);
      }),
      catchError((error, caught) => {
        this.analyticsService.sendUserActionEvent('delete_comment', false);
        throw caught;
      }),
    );
  }

  @Action(OpenTextarea)
  openTextarea(context: StateContext<CommentsStateModel>, { id }: OpenTextarea) {
    context.setState(patch({ openTextarea: id }));
  }

  @Action(CloseTextarea)
  closeTextarea(context: StateContext<CommentsStateModel>) {
    context.setState(patch({ openTextarea: '' }));
  }
}
